import { useTranslation, Trans } from 'react-i18next'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { Controller, useFormContext } from 'react-hook-form'
import { validateFile } from 'components/Pages/SellWine/AddNewOffer/V2/Create/Wine/Left/ByImage/Common/_methods'
import uploadFile from 'utils/uploadFile'
import UploadItem from 'components/Pages/Review/Common/Form/Content/Product/RecognitionModal/Form/LabelsImages/UploadItem'
import { ErrorMessage } from 'components/Common/MasterComponents'
import ImagesContainer from '../ImagesContainer'
import HelpTip from 'components/Common/Tooltips/HelpTip'

const Wrapper = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
  gap: 8px;
`
const AdditionalCount = styled.div`
  font: var(--typography-14-16-semibold);
  color: var(--color-sw-60);
`
const Description = styled.div`
  margin-top: 8px;
  font: var(--typography-12-16-normal);
  color: var(--color-sw-60);
`
const Title = styled.div`
  display: flex;
  gap: 4px;
`

const MAX_IMAGES = 3
const FIELDS = ['first', 'second', 'third']

// Компонент используется и в Common/Modals/RecognizeWine

const AdditionalPhotos = () => {
  const { t } = useTranslation()
  const [loadingStates, setLoadingStates] = useState({
    first: false,
    second: false,
    third: false,
  })

  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    getValues,
    setValue,
    trigger,
  } = useFormContext()

  const additionalImages = getValues('additionalImages') || {}
  const additionalImagesErrors = errors.additionalImages as any

  const handleChange = useCallback(
    async (sourceFiles: FileList) => {
      const files = Array.from(sourceFiles)
      clearErrors('additionalImages')

      // Check if more than 3 files
      if (files.length > MAX_IMAGES) {
        return setError('additionalImages.first', {
          type: 'manual',
          message: t(
            'capturedWines.dialog.updateImages.additionalPhotos.tooMany',
            {
              n: MAX_IMAGES,
            }
          ),
        })
      }

      const filledCount = Object.values(additionalImages).filter(Boolean).length
      if (filledCount + files.length > MAX_IMAGES) {
        return setError('additionalImages.first', {
          type: 'manual',
          message: t(
            'capturedWines.dialog.updateImages.additionalPhotos.tooMany',
            {
              n: MAX_IMAGES,
            }
          ),
        })
      }

      // Find empty slots and fill them
      let fileIndex = 0
      for (let i = 0; i < FIELDS.length && fileIndex < files.length; i++) {
        const fieldName = FIELDS[i]
        if (!additionalImages[fieldName]) {
          const file = files[fileIndex]

          // Проверяем валидацию ДО установки loading состояния
          const validationError = validateFile(file)
          if (validationError) {
            return setError('additionalImages.first', {
              type: 'manual',
              message: validationError,
            })
          }

          setLoadingStates(prev => ({ ...prev, [fieldName]: true }))

          try {
            const uploadResult = await uploadFile(file)
            setValue(`additionalImages.${fieldName}`, uploadResult, {
              shouldDirty: true,
            })
            fileIndex++
          } catch (error: any) {
            setError(`additionalImages.${fieldName}`, {
              type: 'manual',
              message: error?.message,
            })
            return
          } finally {
            setLoadingStates(prev => ({ ...prev, [fieldName]: false }))
          }
        }
      }

      trigger('additionalImages')
    },
    [additionalImages, clearErrors, setError, setValue, trigger, t]
  )

  const filledImagesCount = Object.values(additionalImages || {}).filter(
    Boolean
  ).length

  const firstError =
    additionalImagesErrors &&
    (Object.values(additionalImagesErrors).find(
      (error: any) => error?.message
    ) as any)

  const handleFieldChange = useCallback(
    (fieldName: string) => async (fileOrFiles: File | FileList | null) => {
      if (!fileOrFiles) {
        setValue(`additionalImages.${fieldName}`, null, { shouldDirty: true })
        trigger('additionalImages')
        return
      }

      // If it's FileList (multiple files from first field), use handleChange
      if (fileOrFiles instanceof FileList) {
        return handleChange(fileOrFiles)
      }

      // Single file upload for other fields
      // Проверяем валидацию ДО установки loading состояния
      const validationError = validateFile(fileOrFiles)
      if (validationError) {
        setError(`additionalImages.${fieldName}`, {
          type: 'manual',
          message: validationError,
        })
        return
      }

      setLoadingStates(prev => ({ ...prev, [fieldName]: true }))
      try {
        const uploadResult = await uploadFile(fileOrFiles)
        setValue(`additionalImages.${fieldName}`, uploadResult, {
          shouldDirty: true,
        })
        trigger('additionalImages')
      } catch (error: any) {
        setError(`additionalImages.${fieldName}`, {
          type: 'manual',
          message: error?.message,
        })
      } finally {
        setLoadingStates(prev => ({ ...prev, [fieldName]: false }))
      }
    },
    [handleChange, setError, setValue, trigger]
  )

  return (
    <Wrapper>
      <Title>
        <AdditionalCount>
          {t('capturedWines.dialog.updateImages.additionalCount', {
            n: filledImagesCount,
            max: MAX_IMAGES,
          })}
        </AdditionalCount>
        <HelpTip offset={10} width={256}>
          {t('capturedWines.dialog.updateImages.additionalPhotos.tooltip')}
        </HelpTip>
      </Title>
      <ImagesContainer
        imagesNode={
          <>
            {FIELDS.map(field => (
              <div key={field}>
                {field === 'first' ||
                (field === 'second' &&
                  (additionalImages?.first ||
                    additionalImages?.second ||
                    additionalImages?.third)) ||
                (field === 'third' &&
                  (additionalImages?.first ||
                    additionalImages?.second ||
                    additionalImages?.third)) ? (
                  <Controller
                    name={`additionalImages.${field}`}
                    control={control}
                    render={({ field: { value } }) => (
                      <UploadItem
                        value={value || null}
                        hasError={!!additionalImagesErrors?.[field]}
                        onChange={handleFieldChange(field)}
                        multiple={field === 'first'}
                        loading={loadingStates[field]}
                      />
                    )}
                  />
                ) : null}
              </div>
            ))}
          </>
        }
        descriptionNode={
          <Description>
            <Trans t={t} i18nKey='capturedWines.dialog.updateImages.formats' />
          </Description>
        }
        errorNode={
          firstError && <ErrorMessage>{firstError.message}</ErrorMessage>
        }
      />
    </Wrapper>
  )
}

export default AdditionalPhotos
